// apps/vendor/src/features/orders/BulkAssizeBar.tsx
//
// Amendment #7 — Bulk Assize toolbar on the orders list.
//   Export: download a CSV template (OrderID,ItemID + empty dim columns).
//     TH1 — rows selected → only those item_ids.
//     TH2 — nothing selected → current list filter (status + q).
//   Import: paste/upload the filled CSV → batch-update packaging dims, render
//     imported/failed + per-row {row,field,reason} (shape).
//
// CSV only (xlsx deferred — mirrors products FileImporter "save as CSV").

import { useRef, useState } from 'react';

import { Button } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { assizeExportUrl, useAssizeImport, type AssizeImportResult } from './api.js';

interface Props {
  /** Selected item_ids (TH1). Empty → TH2 uses the current filter. */
  selectedItemIds: string[];
  /** Current list filter (TH2 export parity with GET /vendor/orders). */
  status?: string;
  q?: string;
}

// Parse the filled template CSV → row objects keyed by header. Mirrors the
// products FileImporter parseCsv (1-based row index for error reporting).
function parseCsv(
  input: string,
): Array<Record<string, unknown> & { row: number }> {
  const lines = input
    // dbg (2026-07-07) UAT #2046 — Excel "Save As → CSV UTF-8" prepends a BOM
    // (U+FEFF); without stripping it the first header becomes a BOM+OrderID
    // token so the OrderID column never maps and every row fails "OrderID:
    // INVALID". Match the BOM via \uFEFF escape (a literal BOM is invisible).
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0]!.split(',').map((h) => h.trim());
  const rows: Array<Record<string, unknown> & { row: number }> = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i]!.split(',').map((c) => c.trim());
    const obj: Record<string, unknown> & { row: number } = { row: i + 1 };
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] ?? '';
    });
    rows.push(obj);
  }
  return rows;
}

export function BulkAssizeBar({ selectedItemIds, status, q }: Props): JSX.Element {
  const { t } = useTranslation('orders');
  const importM = useAssizeImport();
  const [showImport, setShowImport] = useState(false);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<AssizeImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  function handleExport(): void {
    const url = assizeExportUrl({
      ...(selectedItemIds.length > 0 ? { item_ids: selectedItemIds } : {}),
      ...(selectedItemIds.length === 0 && status && status !== 'all' ? { status } : {}),
      ...(selectedItemIds.length === 0 && q ? { q } : {}),
    });
    // Credentialed GET → browser downloads the CSV via the Content-Disposition.
    window.open(url, '_blank');
  }

  async function handleFilePick(file: File): Promise<void> {
    setErrorMsg(null);
    // dbg (2026-07-07) UAT #2046 — testers uploaded a raw .xlsx (a ZIP), and
    // file.text() decoded it to binary garbage (starts with "PK") that parsed
    // to nothing with no feedback. Reject non-CSV by extension/MIME and by the
    // ZIP magic bytes, pointing them at Save As → CSV.
    const name = file.name.toLowerCase();
    const looksSpreadsheet =
      name.endsWith('.xlsx') ||
      name.endsWith('.xls') ||
      file.type.includes('spreadsheet') ||
      file.type.includes('excel');
    try {
      const text = await file.text();
      if (looksSpreadsheet || text.startsWith('PK')) {
        setErrorMsg(t('bulkAssize.csvOnly'));
        return;
      }
      setCsv(text);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('bulkAssize.readError'));
    }
  }

  async function handleImport(): Promise<void> {
    setErrorMsg(null);
    setResult(null);
    const rows = parseCsv(csv);
    if (rows.length === 0) {
      setErrorMsg(t('bulkAssize.emptyRows'));
      return;
    }
    try {
      setResult(await importM.mutateAsync(rows));
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('bulkAssize.importFailed'));
    }
  }

  return (
    <div
      data-testid="bulk-assize-bar"
      className="flex flex-col gap-2 rounded-lg border border-pw-border bg-pw-surface p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-pw-text">{t('bulkAssize.title')}</span>
        <span className="text-xs text-pw-text-muted">
          {selectedItemIds.length > 0
            ? t('bulkAssize.scopeSelected', { count: selectedItemIds.length })
            : t('bulkAssize.scopeFilter')}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          data-testid="bulk-assize-export"
          onClick={handleExport}
        >
          {t('bulkAssize.export')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="bulk-assize-import-toggle"
          onClick={() => setShowImport((v) => !v)}
        >
          {t('bulkAssize.import')}
        </Button>
      </div>

      {showImport ? (
        <div className="mt-1 flex flex-col gap-2">
          <p className="text-xs text-pw-text-muted">{t('bulkAssize.pasteHint')}</p>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              data-testid="bulk-assize-file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFilePick(f);
                if (fileRef.current) fileRef.current.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              {t('bulkAssize.pickFile')}
            </Button>
          </div>
          <textarea
            data-testid="bulk-assize-textarea"
            rows={6}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={'OrderID,ItemID,Length,Width,Height,Weight'}
            className="w-full rounded-md border border-pw-border bg-pw-bg p-2 font-mono text-xs text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
          />
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              data-testid="bulk-assize-submit"
              disabled={importM.isPending || csv.trim().length === 0}
              onClick={() => void handleImport()}
            >
              {importM.isPending ? t('bulkAssize.importing') : t('bulkAssize.importSubmit')}
            </Button>
          </div>
        </div>
      ) : null}

      {errorMsg ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMsg}
        </p>
      ) : null}

      {result ? (
        <div className="rounded-md border border-pw-border bg-pw-bg p-2">
          <p className="text-sm font-medium text-pw-text">
            {t('bulkAssize.imported', { imported: result.imported, failed: result.failed })}
          </p>
          {result.errors.length > 0 ? (
            <ul className="mt-1 max-h-40 overflow-auto text-xs text-red-700">
              {result.errors.map((e, i) => (
                <li key={i}>
                  {t('bulkAssize.errorRow', { row: e.row, field: e.field, reason: e.reason })}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default BulkAssizeBar;
